import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames/bind';
import { CalendarOutlined, EnvironmentOutlined, TeamOutlined, CodeOutlined, ToolOutlined } from '@ant-design/icons';
import hustLogo from '../../assets/icons/hust.png';
import sapoLogo from '../../assets/icons/sapo.png';
import vnptItLogo from '../../assets/icons/vnpt-it.png';
import styles from './Experience.module.scss';

const cx = classNames.bind(styles);

function Experience() {
    const [isVisible, setIsVisible] = useState(false);
    const [shapesLoaded, setShapesLoaded] = useState(false);

    // Lấy trạng thái performance từ Redux store
    const highPerformance = useSelector((state) => state.performance.highPerformance);

    useEffect(() => {
        // Hiển thị nội dung ngay lập tức nếu không có hiệu ứng
        if (!highPerformance) {
            setShapesLoaded(true);
            setIsVisible(true);
            return;
        }

        // Xử lý animation với trình tự rõ ràng hơn khi có hiệu ứng
        let timers = [];

        // 1. Đầu tiên hiển thị background
        const shapesTimer = setTimeout(() => {
            setShapesLoaded(true);
        }, 100);
        timers.push(shapesTimer);

        // 2. Sau đó hiển thị nội dung sau khi background đã tải
        const contentTimer = setTimeout(() => {
            setIsVisible(true);
        }, 500);
        timers.push(contentTimer);

        // Xử lý khi tab được click
        const handleTabClick = () => {
            // Khi click tab, reset lại tất cả
            setIsVisible(false);
            setShapesLoaded(false);

            if (highPerformance) {
                // Dùng setTimeout để tạo hiệu ứng mượt mà khi có GPU
                setTimeout(() => {
                    setShapesLoaded(true);

                    // Đợi background xuất hiện rồi mới hiển thị nội dung
                    setTimeout(() => {
                        setIsVisible(true);
                    }, 300);
                }, 100);
            } else {
                // Hiển thị ngay lập tức khi không có hiệu ứng
                setShapesLoaded(true);
                setIsVisible(true);
            }
        };

        // Theo dõi sự kiện click vào tab
        const tabElement = document.querySelector('[href="/kinh-nghiem"]');
        if (tabElement) {
            tabElement.addEventListener('click', handleTabClick);
        }

        // Cleanup
        return () => {
            timers.forEach((timer) => clearTimeout(timer));
            if (tabElement) {
                tabElement.removeEventListener('click', handleTabClick);
            }
        };
    }, [highPerformance]); // Thêm dependency để useEffect chạy lại khi trạng thái performance thay đổi

    return (
        <div className={cx('experience-container', { 'no-animation': !highPerformance })}>
            {/* Background trang trí - chỉ hiển thị khi có hiệu ứng */}
            {highPerformance && (
                <div className={cx('decorative-background', { loaded: shapesLoaded })}>
                    {/* Hình vuông */}
                    <div className={cx('shape', 'square', 'shape-1')}></div>
                    <div className={cx('shape', 'square', 'shape-2')}></div>
                    <div className={cx('shape', 'square', 'shape-3')}></div>
                    <div className={cx('shape', 'square', 'shape-4')}></div>
                    <div className={cx('shape', 'square', 'shape-5')}></div>
                    <div className={cx('shape', 'square', 'shape-6')}></div>

                    {/* Hình tròn */}
                    <div className={cx('shape', 'circle', 'shape-7')}></div>
                    <div className={cx('shape', 'circle', 'shape-8')}></div>
                    <div className={cx('shape', 'circle', 'shape-9')}></div>
                    <div className={cx('shape', 'circle', 'shape-10')}></div>
                    <div className={cx('shape', 'circle', 'shape-11')}></div>

                    {/* Hình tam giác */}
                    <div className={cx('shape', 'triangle', 'shape-12')}></div>
                    <div className={cx('shape', 'triangle', 'shape-13')}></div>
                    <div className={cx('shape', 'triangle', 'shape-14')}></div>

                    {/* Hình chữ nhật */}
                    <div className={cx('shape', 'rectangle', 'shape-15')}></div>
                    <div className={cx('shape', 'rectangle', 'shape-16')}></div>
                    <div className={cx('shape', 'rectangle', 'shape-17')}></div>
                    <div className={cx('shape', 'rectangle', 'shape-18')}></div>

                    {/* Hình thoi */}
                    <div className={cx('shape', 'diamond', 'shape-19')}></div>
                    <div className={cx('shape', 'diamond', 'shape-20')}></div>
                    <div className={cx('shape', 'diamond', 'shape-21')}></div>

                    {/* Hình nhiều cạnh */}
                    <div className={cx('shape', 'hexagon', 'shape-22')}></div>
                    <div className={cx('shape', 'pentagon', 'shape-23')}></div>
                    <div className={cx('shape', 'octagon', 'shape-24')}></div>

                    {/* Hình nửa vòng tròn */}
                    <div className={cx('shape', 'semicircle', 'shape-25')}></div>
                    <div className={cx('shape', 'semicircle', 'shape-26')}></div>

                    {/* Hình dấu cộng */}
                    <div className={cx('shape', 'plus', 'shape-27')}></div>
                    <div className={cx('shape', 'plus', 'shape-28')}></div>

                    {/* Hình sao */}
                    <div className={cx('shape', 'star', 'shape-29')}></div>
                    <div className={cx('shape', 'star', 'shape-30')}></div>

                    {/* Hình donut */}
                    <div className={cx('shape', 'donut', 'shape-31')}></div>
                    <div className={cx('shape', 'donut', 'shape-32')}></div>

                    {/* Đường zigzag */}
                    <div className={cx('shape', 'zigzag', 'shape-33')}></div>
                    <div className={cx('shape', 'zigzag', 'shape-34')}></div>

                    {/* Hình mũi tên */}
                    <div className={cx('shape', 'arrow', 'shape-35')}></div>
                    <div className={cx('shape', 'arrow', 'shape-36')}></div>
                </div>
            )}

            <div
                className={cx('experience-content', {
                    'animate-content': isVisible && highPerformance,
                    'no-animation': !highPerformance,
                })}
            >
                <div className={cx('timeline')}>
                    {/* Job 1 - VNPT IT Fresher */}
                    <div
                        className={cx('timeline-item', {
                            animate: isVisible && highPerformance,
                            'delay-1': isVisible && highPerformance,
                            'no-animation': !highPerformance,
                        })}
                    >
                        <div className={cx('timeline-marker')}></div>
                        <div className={cx('timeline-card', { 'no-hover': !highPerformance })}>
                            {/* Tiêu đề vị trí tách biệt ra */}
                            <h2 className={cx('job-title', 'highlight')}>Fresher Java Backend</h2>

                            {/* Phần chứa logo và thông tin công ty */}
                            <div className={cx('job-header')}>
                                <div className={cx('company-logo')}>
                                    <img src={vnptItLogo} alt="VNPT IT" className={cx('company-image')} />
                                </div>
                                <div className={cx('job-info-section')}>
                                    <div className={cx('company-name')}>
                                        <span>VNPT IT</span>
                                    </div>
                                    <div className={cx('job-meta')}>
                                        <div className={cx('job-period')}>
                                            <CalendarOutlined className={cx('info-icon')} />
                                            <span>09/2024 - Hiện tại</span>
                                        </div>
                                        <div className={cx('job-location')}>
                                            <EnvironmentOutlined className={cx('info-icon')} />
                                            <span>Hà Nội, Việt Nam</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={cx('job-content')}>
                                <div className={cx('job-section')}>
                                    <div className={cx('section-title-wrapper')}>
                                        <div className={cx('section-title')}>
                                            <TeamOutlined className={cx('section-icon')} />
                                            Vai trò & Trách nhiệm
                                        </div>
                                    </div>
                                    <ul className={cx('responsibility-list')}>
                                        <li>
                                            Thiết kế và phát triển các hệ thống <strong>API RESTful</strong> với{' '}
                                            <strong>Spring Boot Microservices</strong>, đảm bảo hiệu suất và khả năng mở
                                            rộng cao.
                                        </li>
                                        <li>
                                            Thực hiện <strong>trigger event</strong> để gửi message qua{' '}
                                            <strong>Apache Kafka</strong>, hỗ trợ giao tiếp giữa các thành phần trong hệ
                                            thống và đảm bảo quá trình xử lý dữ liệu nhanh chóng, chính xác và đồng bộ.
                                        </li>
                                        <li>
                                            Phối hợp với đội <strong>BA</strong> và <strong>Tester</strong> để phân tích
                                            yêu cầu, kiểm thử, xử lý lỗi nhằm cải thiện chất lượng và tính năng của sản
                                            phẩm.
                                        </li>
                                        <li>
                                            Phối hợp với đội <strong>Big Data</strong> và <strong>DevOps</strong> để
                                            tích hợp các tính năng cho dự án, đảm bảo tính đồng bộ và hiệu quả trong quá
                                            trình triển khai.
                                        </li>

                                        <li>
                                            Viết <strong>stored procedures</strong> để truy vấn cơ sở dữ liệu, phục vụ
                                            cho các chức năng trong hệ thống.
                                        </li>
                                        <li>
                                            Debug và xử lý sự cố trên môi trường phát triển <strong>local</strong> cũng
                                            như trên các pod chạy trên nền tảng <strong>Rancher</strong>, đảm bảo hệ
                                            thống hoạt động ổn định và hiệu quả.
                                        </li>
                                        <li>
                                            Triển khai các quy trình <strong>backup</strong>,{' '}
                                            <strong>di chuyển dữ liệu</strong>, và <strong>đồng bộ hóa</strong> giữa các
                                            hệ thống cơ sở dữ liệu khác nhau, đáp ứng nhu cầu mở rộng và chuyển đổi hạ
                                            tầng hệ thống.
                                        </li>
                                        <li>
                                            Tham gia hỗ trợ nhiều dự án quan trọng, đảm bảo tiến độ và chất lượng trong
                                            phát triển sản phẩm.
                                        </li>
                                    </ul>
                                </div>

                                <div className={cx('job-section')}>
                                    <div className={cx('section-title-wrapper')}>
                                        <div className={cx('section-title')}>
                                            <CodeOutlined className={cx('section-icon')} />
                                            Công nghệ sử dụng
                                        </div>
                                    </div>
                                    <div className={cx('tech-tags')}>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Java
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Spring Boot
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Spring AOP
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Spring Security
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Spring Cloud
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Netflix Eureka
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Spring Webflux
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Kafka
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            MinIO
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Keycloak
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            MySQL
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            PostgreSQL
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Oracle
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            MongoDB
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Redis
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            SSE
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Multi-thread
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            CI GitLab
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Docker
                                        </span>
                                    </div>
                                </div>

                                <div className={cx('job-section')}>
                                    <div className={cx('section-title-wrapper')}>
                                        <div className={cx('section-title')}>
                                            <ToolOutlined className={cx('section-icon')} />
                                            Tools
                                        </div>
                                    </div>
                                    <div className={cx('tech-tags')}>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            IntelliJ
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Postman
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            DataGrip
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            VM Ware
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Dbeaver
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Docker Desktop
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Job 2 - VNPT IT Intern */}
                    <div
                        className={cx('timeline-item', {
                            animate: isVisible && highPerformance,
                            'delay-2': isVisible && highPerformance,
                            'no-animation': !highPerformance,
                        })}
                    >
                        <div className={cx('timeline-marker')}></div>
                        <div className={cx('timeline-card', { 'no-hover': !highPerformance })}>
                            {/* Tiêu đề vị trí tách biệt ra */}
                            <h2 className={cx('job-title', 'highlight')}>Intern Java Backend</h2>

                            {/* Phần chứa logo và thông tin công ty */}
                            <div className={cx('job-header')}>
                                <div className={cx('company-logo')}>
                                    <img src={vnptItLogo} alt="VNPT IT" className={cx('company-image')} />
                                </div>
                                <div className={cx('job-info-section')}>
                                    <div className={cx('company-name')}>
                                        <span>VNPT IT</span>
                                    </div>
                                    <div className={cx('job-meta')}>
                                        <div className={cx('job-period')}>
                                            <CalendarOutlined className={cx('info-icon')} />
                                            <span>03/2024 - 06/2024</span>
                                        </div>
                                        <div className={cx('job-location')}>
                                            <EnvironmentOutlined className={cx('info-icon')} />
                                            <span>Hà Nội, Việt Nam</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={cx('job-content')}>
                                <div className={cx('job-section')}>
                                    <div className={cx('section-title-wrapper')}>
                                        <div className={cx('section-title')}>
                                            <TeamOutlined className={cx('section-icon')} />
                                            Vai trò & Trách nhiệm
                                        </div>
                                    </div>
                                    <ul className={cx('responsibility-list')}>
                                        <li>
                                            Học hỏi và áp dụng các <strong>Design Patterns</strong> như Singleton,
                                            Factory, Observer để giải quyết các vấn đề trong phát triển phần mềm
                                        </li>
                                        <li>
                                            Làm quen với hệ điều hành <strong>Linux</strong>, học cách vận hành và triển
                                            khai hệ thống trên VMWare
                                        </li>
                                        <li>
                                            Tăng cường kỹ năng lập trình backend với <strong>Spring Boot</strong> và
                                            kiến trúc
                                            <strong> Microservices</strong>
                                        </li>
                                        <li>Học cách cải tiến các chức năng hệ thống và tối ưu hiệu suất mã nguồn</li>
                                        <li>
                                            Học và thực hiện quy trình tích hợp và triển khai liên tục (
                                            <strong>CI/CD</strong>) với các công cụ DevOps
                                        </li>
                                    </ul>
                                </div>

                                <div className={cx('job-section')}>
                                    <div className={cx('section-title-wrapper')}>
                                        <div className={cx('section-title')}>
                                            <CodeOutlined className={cx('section-icon')} />
                                            Công nghệ sử dụng
                                        </div>
                                    </div>
                                    <div className={cx('tech-tags')}>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Java
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Spring Boot
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Spring AOP
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Spring Security
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Spring Cloud
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Netflix Eureka
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            MySQL
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Ubuntu
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Nginx
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            CI GitLab
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Docker
                                        </span>
                                    </div>
                                </div>

                                <div className={cx('job-section')}>
                                    <div className={cx('section-title-wrapper')}>
                                        <div className={cx('section-title')}>
                                            <ToolOutlined className={cx('section-icon')} />
                                            Tools
                                        </div>
                                    </div>
                                    <div className={cx('tech-tags')}>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            IntelliJ
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Postman
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            DataGrip
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            VM Ware
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Dbeaver
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Docker Desktop
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Job 3 - Sapo Intern */}
                    <div
                        className={cx('timeline-item', {
                            animate: isVisible && highPerformance,
                            'delay-3': isVisible && highPerformance,
                            'no-animation': !highPerformance,
                        })}
                    >
                        <div className={cx('timeline-marker')}></div>
                        <div className={cx('timeline-card', { 'no-hover': !highPerformance })}>
                            {/* Tiêu đề vị trí tách biệt ra */}
                            <h2 className={cx('job-title', 'highlight')}>Intern Java Backend</h2>

                            {/* Phần chứa logo và thông tin công ty */}
                            <div className={cx('job-header')}>
                                <div className={cx('company-logo')}>
                                    <img src={sapoLogo} alt="Sapo" className={cx('company-image')} />
                                </div>
                                <div className={cx('job-info-section')}>
                                    <div className={cx('company-name')}>
                                        <span>Sapo</span>
                                    </div>
                                    <div className={cx('job-meta')}>
                                        <div className={cx('job-period')}>
                                            <CalendarOutlined className={cx('info-icon')} />
                                            <span>07/2023 - 09/2023</span>
                                        </div>
                                        <div className={cx('job-location')}>
                                            <EnvironmentOutlined className={cx('info-icon')} />
                                            <span>Hà Nội, Việt Nam</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={cx('job-content')}>
                                <div className={cx('job-section')}>
                                    <div className={cx('section-title-wrapper')}>
                                        <div className={cx('section-title')}>
                                            <TeamOutlined className={cx('section-icon')} />
                                            Vai trò & Trách nhiệm
                                        </div>
                                    </div>
                                    <ul className={cx('responsibility-list')}>
                                        <li>
                                            Hợp tác cùng nhóm (4 thành viên) để phát triển hệ thống website bán hàng tại
                                            quầy, mô phỏng các tính năng chính của <strong>Sapo POS</strong> dựa trên
                                            yêu cầu từ trưởng nhóm dự án.
                                        </li>
                                        <li>
                                            Xây dựng backend với <strong>Spring Boot</strong>, thiết kế và tối ưu cơ sở
                                            dữ liệu bằng <strong>MySQL</strong>, đồng thời tích hợp frontend phát triển
                                            bằng <strong>ReactJS</strong>.
                                        </li>
                                        <li>
                                            Thực hiện theo quy trình làm việc thực tế, sử dụng <strong>GitLab</strong>{' '}
                                            để quản lý mã nguồn hiệu quả và triển khai chiến lược{' '}
                                            <strong>Git Flow</strong>, đảm bảo tính nhất quán, chất lượng mã nguồn và
                                            hiệu quả trong quá trình phát triển dự án.
                                        </li>
                                        <li>
                                            Tích cực trao đổi và phối hợp với các thành viên trong nhóm để đảm bảo tiến
                                            độ, xử lý các vấn đề kỹ thuật phát sinh và nâng cao chất lượng sản phẩm.
                                        </li>
                                        <li>
                                            Nghiên cứu và áp dụng các kỹ thuật lập trình nâng cao, nâng cao kỹ năng giải
                                            quyết vấn đề trong môi trường phát triển dự án thực tế.
                                        </li>
                                    </ul>
                                </div>

                                <div className={cx('job-section')}>
                                    <div className={cx('section-title-wrapper')}>
                                        <div className={cx('section-title')}>
                                            <CodeOutlined className={cx('section-icon')} />
                                            Công nghệ sử dụng
                                        </div>
                                    </div>
                                    <div className={cx('tech-tags')}>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            SCSS
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            ReactJS
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Material-UI
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Redux
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Java
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Spring Boot
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Spring AOP
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Spring Security
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            MySQL
                                        </span>
                                    </div>
                                </div>

                                <div className={cx('job-section')}>
                                    <div className={cx('section-title-wrapper')}>
                                        <div className={cx('section-title')}>
                                            <ToolOutlined className={cx('section-icon')} />
                                            Tools
                                        </div>
                                    </div>
                                    <div className={cx('tech-tags')}>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            IntelliJ
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Postman
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            MySQL Workbench
                                        </span>
                                        <span className={cx('tech-tag', { 'no-hover-animation': !highPerformance })}>
                                            Visual Studio Code
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Job 4 - Đại học Bách Khoa Hà Nội */}
                    <div
                        className={cx('timeline-item', {
                            animate: isVisible && highPerformance,
                            'delay-4': isVisible && highPerformance,
                            'no-animation': !highPerformance,
                        })}
                    >
                        <div className={cx('timeline-marker')}></div>
                        <div className={cx('timeline-card')}>
                            {/* Tiêu đề vị trí tách biệt ra */}
                            <h2 className={cx('job-title', 'highlight')}>Các đồ án tiêu biểu</h2>

                            {/* Phần chứa logo và thông tin công ty */}
                            <div className={cx('job-header')}>
                                <div className={cx('company-logo')}>
                                    <img src={hustLogo} alt="HUST" className={cx('company-image')} />
                                </div>
                                <div className={cx('job-info-section')}>
                                    <div className={cx('company-name')}>
                                        <span>Đại học Bách Khoa Hà Nội</span>
                                    </div>
                                    <div className={cx('job-meta')}>
                                        <div className={cx('job-period')}>
                                            <CalendarOutlined className={cx('info-icon')} />
                                            <span>2020 - 2024</span>
                                        </div>
                                        <div className={cx('job-location')}>
                                            <EnvironmentOutlined className={cx('info-icon')} />
                                            <span>Hà Nội, Việt Nam</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={cx('job-content')}>
                                <div className={cx('job-section')}>
                                    <div className={cx('section-title-wrapper')}>
                                        <div className={cx('section-title')}>
                                            <TeamOutlined className={cx('section-icon')} />
                                            Các dự án
                                        </div>
                                    </div>
                                    <ul className={cx('responsibility-list')}>
                                        <li>
                                            <strong>SmartForm - Hệ thống quản lý form đa năng</strong> (Đồ án tốt
                                            nghiệp)
                                            <ul>
                                                <li>
                                                    <strong>Mô tả</strong>: Hệ thống quản lý form thông minh với tính
                                                    năng tạo, chỉnh sửa, phân tích dữ liệu, nhận diện khuôn mặt, ...
                                                </li>
                                                <li>
                                                    <strong>Công nghệ sử dụng</strong>:
                                                    <div className={cx('tech-tags')}>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            ReactJS
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Ant Design
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Redux
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Spring Boot
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Spring AOP
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Python
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Flask
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Kafka
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            MinIO
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            FCM
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            WebSocket
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Elasticsearch
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Redis
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            ...
                                                        </span>
                                                    </div>
                                                </li>
                                                <li>
                                                    <strong>Tools</strong>:
                                                    <div className={cx('tech-tags')}>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Postman
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            MySQL Workbench
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            IntelliJ
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Docker Desktop
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Swagger
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Astah UML
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Visual Studio Code
                                                        </span>
                                                    </div>
                                                </li>
                                            </ul>
                                        </li>
                                        <li>
                                            <strong>Xây dựng ứng dụng đa nền tảng với giao diện giống Facebook</strong>
                                            <ul>
                                                <li>
                                                    <strong>Mô tả</strong>: Sử dụng <strong>React Native Expo</strong>{' '}
                                                    để xây dựng ứng dụng mạng xã hội với giao diện và tính năng cơ bản
                                                    giống Facebook
                                                </li>
                                                <li>
                                                    <strong>Công nghệ sử dụng</strong>:
                                                    <div className={cx('tech-tags')}>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            React Native Expo
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            FCM
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Redux
                                                        </span>
                                                    </div>
                                                </li>
                                                <li>
                                                    <strong>Tools</strong>:
                                                    <div className={cx('tech-tags')}>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Postman
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Visual Studio Code
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Expo
                                                        </span>
                                                    </div>
                                                </li>
                                            </ul>
                                        </li>
                                        <li>
                                            <strong>Xây dựng game cờ vua</strong>
                                            <ul>
                                                <li>
                                                    <strong>Mô tả</strong>: Thiết kế trò chơi cờ vua với giao diện trực
                                                    quan, xử lý logic trò chơi
                                                </li>
                                                <li>
                                                    <strong>Công nghệ sử dụng</strong>:
                                                    <div className={cx('tech-tags')}>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Python
                                                        </span>
                                                    </div>
                                                </li>
                                                <li>
                                                    <strong>Tools</strong>:
                                                    <div className={cx('tech-tags')}>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            Pycharm
                                                        </span>
                                                    </div>
                                                </li>
                                            </ul>
                                        </li>
                                        <li>
                                            <strong>Quản lý dân cư</strong>
                                            <ul>
                                                <li>
                                                    <strong>Mô tả</strong>: Phát triển ứng dụng desktop sử dụng{' '}
                                                    <strong>JavaFX</strong>, quản lý và tìm kiếm thông tin dân cư
                                                </li>
                                                <li>
                                                    <strong>Công nghệ sử dụng</strong>:
                                                    <div className={cx('tech-tags')}>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            JavaFX
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            SceneBuilder
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            MySQL
                                                        </span>
                                                    </div>
                                                </li>
                                                <li>
                                                    <strong>Tools</strong>:
                                                    <div className={cx('tech-tags')}>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            NetBeans
                                                        </span>
                                                        <span
                                                            className={cx('tech-tag', {
                                                                'no-hover-animation': !highPerformance,
                                                            })}
                                                        >
                                                            XAMPP phpMyAdmin
                                                        </span>
                                                    </div>
                                                </li>
                                            </ul>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Experience;
